import React from "react";
import {
    Scissors,
    Sprout,
    Leaf,
    TreeDeciduous,
    Trash2,
    Droplets,
    Brush,
    Wrench,
    Layers,
    ArrowUpRight,
} from "lucide-react";

export const services = [
    {
        slug: "lawn-mowing",
        n: "01",
        icon: Scissors,
        title: "Lawn mowing",
        body: "Weekly, fortnightly or one-off mows. Clean edges, crisp lines, and we take the clippings with us.",
        tags: ["Edged", "Catch-and-go", "All grass types"],
    },
    {
        slug: "garden-maintenance",
        n: "02",
        icon: Sprout,
        title: "Garden maintenance",
        body: "Regular tidy-ups, pruning and mulching to keep beds, borders and pots looking sharp through every season.",
        tags: ["Pruning", "Mulching", "Seasonal"],
    },
    {
        slug: "weeding",
        n: "03",
        icon: Leaf,
        title: "Weeding",
        body: "Beds, paths, driveways and cracks. Hand-pulled or treated — we tell you what we used and why.",
        tags: ["Beds", "Paths", "Treated"],
    },
    {
        slug: "hedge-trimming",
        n: "04",
        icon: TreeDeciduous,
        title: "Hedge trimming",
        body: "Sharp, level hedges with proper top, sides and base lines. Clippings cleared, plants protected.",
        tags: ["Shape cut", "Topiary tidy", "Heights"],
    },
    {
        slug: "yard-cleanups",
        n: "05",
        icon: Trash2,
        title: "Yard cleanups",
        body: "Leaves, debris, storm mess, post-tenant clears. We turn up with bags, blowers and a trailer.",
        tags: ["Seasonal", "Post-storm", "Move-out"],
    },
    {
        slug: "pressure-washing",
        n: "06",
        icon: Droplets,
        title: "Pressure washing",
        body: "Driveways, paths, decks, fences, retaining walls. We adjust pressure to suit the surface so nothing gets chewed.",
        tags: ["Driveways", "Decks", "Fences"],
    },
    {
        slug: "outdoor-cleaning",
        n: "07",
        icon: Brush,
        title: "Outdoor cleaning",
        body: "Gutter clears, soffit and exterior wipe-downs, outdoor furniture, BBQ areas and entryways.",
        tags: ["Gutters", "Furniture", "Entries"],
    },
    {
        slug: "general-property-maintenance",
        n: "08",
        icon: Wrench,
        title: "General property maintenance",
        body: "The odd jobs that pile up — fence panels, hose taps, garden taps, rubbish runs, small repairs. Ask us.",
        tags: ["Odd jobs", "Repairs", "Trips to tip"],
    },
    {
        slug: "lawn-returfing",
        n: "09",
        icon: Layers,
        title: "Lawn returfing",
        body: "Tired, patchy or thinning lawn? We strip the old, prep the soil, and lay fresh turf for a clean restart.",
        tags: ["Prep & lay", "Fresh turf", "Whole-lawn"],
    },
];

export default function Services() {
    return (
        <section
            id="services"
            data-testid="section-services"
            className="relative bg-[var(--paper)] pb-28 md:pb-40 border-t border-line pt-20 md:pt-28"
        >
            <div className="maxw section-pad">
                <div className="grid md:grid-cols-12 gap-12 mb-14 items-end">
                    <div className="md:col-span-7 reveal">
                        <div className="eyebrow mb-5">Services</div>
                        <h2 className="font-display font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                            Everything outside your home —{" "}
                            <span className="italic text-[var(--terra)]">
                                handled
                            </span>
                            .
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:text-right">
                        <p className="text-[rgba(14,14,16,0.72)] text-[15px] leading-relaxed max-w-md md:ml-auto">
                            One-off jobs or regular visits — we’ll quote, schedule,
                            and finish neat. Mix and match anything below.
                        </p>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line)] border border-line">
                    {services.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <article
                                key={s.slug}
                                data-testid={`service-card-${s.slug}`}
                                className="reveal bg-[var(--paper)] p-7 md:p-8 group cursor-pointer relative transition-colors duration-300 hover:bg-[var(--paper-2)] flex flex-col gap-6 min-h-[300px]"
                                style={{ transitionDelay: `${i * 60}ms` }}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="w-10 h-10 rounded-xl bg-[rgba(63,90,36,0.12)] text-[var(--terra)] flex items-center justify-center group-hover:bg-[var(--terra)] group-hover:text-[var(--paper)] transition-colors">
                                            <Icon size={18} strokeWidth={1.7} />
                                        </span>
                                        <span className="pillar-num">{s.n}</span>
                                    </div>
                                    <ArrowUpRight
                                        size={20}
                                        className="text-[rgba(14,14,16,0.38)] group-hover:text-[var(--terra)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition"
                                    />
                                </div>

                                <div className="flex-1 flex flex-col gap-3">
                                    <h3 className="font-display text-2xl font-medium leading-tight tracking-tight text-[var(--ink)]">
                                        {s.title}
                                    </h3>
                                    <p className="text-[14.5px] leading-relaxed text-[rgba(14,14,16,0.68)]">
                                        {s.body}
                                    </p>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {s.tags.map((t) => (
                                        <span
                                            key={t}
                                            className="px-2.5 py-1 rounded-full text-[11px] font-mono-x bg-[rgba(14,14,16,0.06)] text-[rgba(14,14,16,0.72)] border border-line"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </article>
                        );
                    })}
                </div>

                <div className="mt-12 flex flex-wrap items-center justify-between gap-4">
                    <p className="text-[rgba(14,14,16,0.6)] text-[14px]">
                        Not sure which one fits? Just describe the job — we’ll
                        figure it out.
                    </p>
                    <a href="#contact" className="btn-ink" data-testid="services-cta">
                        Get a free quote
                        <span>→</span>
                    </a>
                </div>
            </div>
        </section>
    );
}
